import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from "@mui/material";
import api from "../../../services/api";
import { showBasicAlert } from "../../../core/common/useAlert";

interface FormField {
  id: number;
  label: string;
  type: "text" | "select";
  options?: string[];
}

interface FormViewerProps {
  open: boolean;
  onClose: () => void;
  data: any[];
}

const LeadFormViewer: React.FC<FormViewerProps> = ({ open, onClose, data }) => {
  const [selectedForm, setSelectedForm] = useState<any>(null);
  const [responses, setResponses] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false); // state to track loading

  useEffect(() => {
    if (data?.length > 0) {
      setSelectedForm(data[0]); // Auto-select first form
      setResponses({}); // Reset responses
    }
  }, [data]);

  const [countries, setCountries] = useState<any[]>([]);
  const [CountryCode, setCountryCode] = useState<any[]>([]);

  const [statesOrignal, setStatesOrignal] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);

  const [citiesOrignal, setCitiesOrignal] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);

  const [distOrignal, setDistOrignal] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);

  useEffect(() => {
    const signature = localStorage.getItem("signature");

    // This will run when the component mounts
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        const [countryResponse, stateResponse] = await Promise.all([
          api.post("userService/getCountry", { signature }),
          api.post("userService/getState", { signature }),
        ]);
        setCountryCode(countryResponse.data.data);
        setCountries(countryResponse.data.data);
        setStatesOrignal(stateResponse.data.data);
        setIsLoading(false);

        var cityResponse = await api.post("userService/getCity", { signature });
        setCitiesOrignal(cityResponse.data.data);

        var districtResponse = await api.post("userService/getDistrict", {
          signature,
        });
        setDistOrignal(districtResponse.data.data);
      } catch (err: any) {
        showBasicAlert(err.data.message[0].msg);
        console.error("Error fetching dashboard data:", err);
      }
    };

    fetchDashboardData();
  }, []);

  const handleCountryChange = (e: any) => {
    const countryId = e.target.value;
    // Filter states based on country selection
    const filteredStates = statesOrignal.filter(
      (state) => state.country_id == countryId
    );
    setStates(filteredStates);
  };

  const handleStateChange = (e: any) => {
    const stateId = e.target.value;
    const filteredCities = citiesOrignal.filter(
      (city) => city.state_id == stateId
    );
    setCities(filteredCities);
    const filteredDisy = distOrignal.filter((city) => city.state_id == stateId);
    setDistricts(filteredDisy);
  };

  const handleCityChange = (e: any) => {
    const cityId = e.target.value;
    // Filter cities based on state selection
  };

  const handleDistChange = (e: any) => {
    const distId = e.target.value;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      ></DialogTitle>

      <DialogContent>
        <h3>{data?.[0]?.LeadFormName}</h3>
        <br></br>
        {selectedForm &&
          selectedForm.structure.map((field: FormField) => (
            <Box key={field.id} sx={{ mb: 2 }}>
              {field.type === "text" ? (
                <FormControl fullWidth size="small">
                  <label className="form-label">
                    {field.label} <span className="text-danger"> *</span>
                  </label>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    onChange={(e) =>
                      setResponses({
                        ...responses,
                        [field.id]: e.target.value,
                      })
                    }
                  />
                </FormControl>
              ) : field.type === "select" ? (
                <FormControl fullWidth size="small">
                  <label className="form-label">
                    {field.label} <span className="text-danger"> *</span>
                  </label>
                  <Select
                    value={responses[field.id] || ""}
                    onChange={(e) =>
                      setResponses({
                        ...responses,
                        [field.id]: e.target.value,
                      })
                    }
                  >
                    {field.options?.map((option, index) => (
                      <MenuItem key={index} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : field.type === "date" ? (
                <FormControl fullWidth size="small">
                  <label className="form-label">
                    {field.label} <span className="text-danger"> *</span>
                  </label>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    onChange={(e) =>
                      setResponses({
                        ...responses,
                        [field.id]: e.target.value,
                      })
                    }
                    type="date"
                  />
                </FormControl>
              ) : field.type === "country" ? (
                <FormControl fullWidth size="small">
                  <div className="row">
                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">
                          Country <span className="text-danger"> *</span>
                        </label>
                        <select
                          className="form-control"
                          name="country"
                          onChange={handleCountryChange}
                        >
                          <option value="">Select Country</option>
                          {countries.map((country) => (
                            <option key={country.id} value={country.id}>
                              {country.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* State */}
                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">
                          State <span className="text-danger"> *</span>
                        </label>
                        <select
                          className="form-control"
                          name="state"
                          onChange={handleStateChange}
                          disabled={states.length == 0}
                        >
                          <option value="">Select State</option>
                          {states.map((state) => (
                            <option key={state.id} value={state.id}>
                              {state.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* District */}
                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">
                          District <span className="text-danger"> *</span>
                        </label>
                        <select
                          className="form-control"
                          name="city"
                          onChange={handleDistChange}
                          disabled={districts.length == 0}
                        >
                          <option value="">Select District</option>
                          {districts.map((dist) => (
                            <option key={dist.district} value={dist.id}>
                              {dist.district}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* City */}
                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">
                          City <span className="text-danger"> *</span>
                        </label>
                        <select
                          className="form-control"
                          name="city"
                          onChange={handleCityChange}
                          disabled={cities.length == 0}
                        >
                          <option value="">Select City</option>
                          {cities.map((city) => (
                            <option key={city.id} value={city.id}>
                              {city.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </FormControl>
              ) : null}
            </Box>
          ))}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        {/* <Button onClick={handleSubmit} variant="contained" color="primary">
          Submit
        </Button> */}
      </DialogActions>
    </Dialog>
  );
};

export default LeadFormViewer;
